/**
 * 가동율 이력 분석 페이지
 * - 기간·품목(차종/품명/품번) 필터로 모델별 가동율 차트 표시
 * - getUtilBar API 호출 후 BarChart로 가동율(%) 막대 시각화
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router';
import moment from 'moment';
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateOnlyPickerStart, CustomDateOnlyPickerEnd } from '../../components/CustomDatePicker';
import Select, { components } from 'react-select';
import { Bar, Line, ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, LabelList, ReferenceLine, Tooltip, Label, Customized } from 'recharts';
import filledArrowIconImg from '../../../images/filledArrowIcon.png';
import resetIconImg from '../../../images/resetIcon.png';
import NoData from '../../components/NoData';
import './operationRate.scss';
import { getCurrentProcessStartedAt, getModelTypes, getModelNames, getModelNos, getUtilBar, getWorkerBar, getDailyDowntime } from '../../../api/modelApi';
import TotalLabelOnTopSegment from './components/TotalLabelOnTopSegment';

/* ---- 차트 시각화 상수 ---- */
const CHART_HEIGHT = 350;
const BAR_WIDTH_PER_ITEM_PX = 56;
const CHART_MIN_WIDTH_PX = 400;
const XAXIS_MAX_VISIBLE_TICKS = 10;   // 한 번에 보여줄 X축 tick 최대 개수(디자인 기준)
const XAXIS_TICK_FONT_SIZE = 12;
const BAR_CATEGORY_GAP_PERCENT = 8;
const LABEL_LIMIT_THRESHOLD = 30;
const LABEL_MAX_ONLY_THRESHOLD = 50;
const BAR_FILL_OPACITY = 0.65;
const CHART_PLOT_TOP = 20;
const CHART_X_AXIS_HEIGHT = 56;
const CHART_PLOT_BOTTOM = CHART_HEIGHT - 24 - CHART_X_AXIS_HEIGHT;
const CHART_PLOT_HEIGHT = CHART_PLOT_BOTTOM - CHART_PLOT_TOP;



/** 가동율 직선 위 % 라벨 */
function UtilRateLineLabel({ x, y, value, yOffset = 24 }) {
    const rate = value != null ? Number(value) : NaN;
    if (rate == null || isNaN(rate)) return null;
    return (
        <text x={x} y={y - yOffset} textAnchor="middle" fill="#C04F15" fontSize={12} fontFamily="NanumSquare">
            {`${rate.toFixed(1)}%`}
        </text>
    );
}

function getLabelDisplayMode(length) {
    if (length > LABEL_MAX_ONLY_THRESHOLD) return 'max';
    if (length > LABEL_LIMIT_THRESHOLD) return 'alternate';
    return 'all';
}

function shouldShowChartLabel(policy, idx) {
    if (!policy || idx < 0) return false;
    if (policy.mode === 'max') return idx === policy.maxIdx;
    if (policy.mode === 'alternate') return idx % 2 === 0;
    return true;
}

/** 비가동 시간 포맷: 초 → "Xh XXm" (차트·툴팁 공통) */
function formatDurationSeconds(sec) {
    const s = Math.round(Number(sec) || 0);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
}

/** 비가동 스택 막대: 흰색(위) 세그먼트 안에 "총 비가동 Xh XXm" 표시 - 흰 박스 + 검정 글자 */
function DowntimeTotalLabel({ x, y, width, height, value }) {
    const totalHours = value != null ? Number(value) : 0;
    const totalSec = totalHours * 3600;
    if (totalSec <= 0) return null;
    const text = '총 비가동';
    const duration = formatDurationSeconds(totalSec);
    const showInside = height >= 24;
    const cx = x + width / 2;
    const lineHeight = 12;
    const fill = '#000000';
    const boxW = 60;
    const boxH = 32;
    const boxX = cx - boxW / 2;
    if (showInside) {
        const yPos = y + height / 2;
        const boxY = yPos - 16;
        return (
            <g>
                <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={4} ry={4} fill="rgba(255,255,255,0.7)" />
                <text x={cx} y={y + height / 2 - lineHeight / 2} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">총 비가동</text>
                <text x={cx} y={y + height / 2 + lineHeight / 2} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">{formatDurationSeconds(totalSec)}</text>
            </g>
        );
    }
    const outsideY = y - 20;
    const boxY = outsideY - 16;
    return (
        <g>
            <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={4} ry={4} fill="rgba(255,255,255,0.7)" />
            <text x={cx} y={outsideY} textAnchor="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">{text}</text>
            <text x={cx} y={outsideY + lineHeight} textAnchor="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">{duration}</text>
        </g>
    );
}

/** 비가동 스택 막대: 계획(총 비가동) 세그먼트 라벨 - 차트에는 미표시, 툴팁에만 사용 */
// function DowntimePlannedLabel({ x, y, width, height, value }) {
//     return null;
// }

/** 비가동 스택 막대: 계획 외 비가동(파란) 세그먼트. 막대(세그먼트) 높이가 낮을 때만 막대 오른쪽에 파란 박스+흰 글자, 아니면 기존(막대 안/위 흰 박스+검정 글자) */
const UNPLANNED_BAR_COLOR = '#0A2B83';
const UNPLANNED_LABEL_BG = 'rgb(10, 43, 131)';
const UNPLANNED_LABEL_OFFSET_RIGHT = 10;
/** 이 값 미만이면 세그먼트 높이 낮은 걸로 보고 라벨을 막대 오른쪽으로 뺌 */
const DOWNTIME_LOW_SEGMENT_HEIGHT_PX = 28;

function DowntimeUnplannedLabel({ x, y, width, height, value }) {
    const unplannedHours = value != null ? Number(value) : 0;
    const totalSec = unplannedHours * 3600;
    if (totalSec <= 0) return null;
    const lineHeight = 12;
    const fontSize = 12;
    const segmentHeightPx = height != null ? Number(height) : 0;
    const useRightSide = segmentHeightPx < DOWNTIME_LOW_SEGMENT_HEIGHT_PX;

    if (useRightSide) {
        const boxW = 88;
        const boxH = 36;
        const boxX = x + width + UNPLANNED_LABEL_OFFSET_RIGHT;
        const centerY = y + height / 2;
        const boxY = centerY - boxH / 2;
        const line1Y = centerY - lineHeight / 2;
        const line2Y = centerY + lineHeight / 2;
        return (
            <g>
                <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={4} ry={4} fill={UNPLANNED_LABEL_BG} stroke={UNPLANNED_BAR_COLOR} strokeWidth={1} />
                <text x={boxX + boxW / 2} y={line1Y} textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize={fontSize} fontFamily="NanumSquare" fontWeight={600}>계획 외 비가동</text>
                <text x={boxX + boxW / 2} y={line2Y} textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize={fontSize} fontFamily="NanumSquare" fontWeight={600}>{formatDurationSeconds(totalSec)}</text>
            </g>
        );
    }

    const text = '계획 외 비가동';
    const duration = formatDurationSeconds(totalSec);
    const MIN_HEIGHT_INSIDE = 28;
    const showInside = height >= MIN_HEIGHT_INSIDE;
    const cx = x + width / 2;
    const fill = '#000000';
    const boxW = 80;
    const boxH = 32;
    const boxX = cx - boxW / 2;
    if (showInside) {
        const yPos = y + height / 2;
        const boxY = yPos - 16;
        return (
            <g>
                <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={4} ry={4} fill="rgba(255,255,255,0.7)" />
                <text x={cx} y={y + height / 2 - lineHeight / 2} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">계획 외 비가동</text>
                <text x={cx} y={y + height / 2 + lineHeight / 2} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">{duration}</text>
            </g>
        );
    }
    const outsideY = y - 56;
    const boxY = outsideY - 16;
    return (
        <g>
            <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={4} ry={4} fill="rgba(255,255,255,0.7)" />
            <text x={cx} y={outsideY} textAnchor="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">{text}</text>
            <text x={cx} y={outsideY + lineHeight} textAnchor="middle" fill={fill} fontSize={12} fontFamily="NanumSquare">{duration}</text>
        </g>
    );
}

/** 비가동 계획(총 비가동) 막대용 커스텀 shape: 연속된 실선 테두리 (Recharts 기본 stroke 점선 현상 방지) */
function DowntimePlannedBarShape(props) {
    const { x, y, width, height } = props;
    if (height <= 0 || width <= 0) return null;
    return (
        <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="rgba(255,255,255,0.9)"
            stroke="#0A2B83"
            strokeWidth={0.5}
            style={{ shapeRendering: 'crispEdges' }}
        />
    );
}

function CenterYAxisLabel({ viewBox, value, fill, side = 'left' }) {
    if(!viewBox) return null;
    const { x, y, width, height } = viewBox;
    const cy = y + height / 2;

    // 축 밖으로 살짝 빼기(원하면 숫자만 조절)
    const pad = 32;
    const cx = side === 'left' ? x - pad : x + width + pad;

    return (
        <text
            x={cx}
            y={cy}
            fill={fill}
            textAnchor="middle"
            dominantBaseline="middle"
            transform={`rotate(-90, ${cx}, ${cy})`}
            style={{ fontFamily: 'NanumSquare' }}
        >
            {value}
        </text>
    );
}

function UtilCustomTooltip({ active, payload, label, analysisView }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload || {};
    const rate = Number(d.utilRate != null ? d.utilRate : d.operatingRate);
    const totalProduce = Number(d.totalProduceSum) || 0;
    const dateText = typeof label === 'string' ? label.split('T')[0].replace(/-/g, '.') : String(label ?? '-');
    const isWorker = analysisView === 'worker';
    const isDowntime = d.totalDuration != null || d.plannedHours != null || d.unplannedHours != null;
    return (
        <div style={{ background: '#fff', border: '1px solid #ddd', padding: 8, fontFamily: 'NanumSquare' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{isWorker ? '작업자' : '날짜'}: {isWorker ? (d.workerName ?? label ?? '-') : dateText}</div>
            {isDowntime ? (
                <>
                    <div style={{ color: '#C04F15' }}>가동율: {Number.isFinite(rate) ? `${rate.toFixed(1)}%` : '-'}</div>
                    <div style={{ color: '#666' }}>계획 비가동: {formatDurationSeconds((d.plannedDuration || 0) || (d.plannedHours || 0) * 3600)}</div>
                    <div style={{ color: '#0A2B83' }}>계획 외 비가동: {formatDurationSeconds((d.unplannedDuration || 0) || (d.unplannedHours || 0) * 3600)}</div>
                    <div>총 비가동: {formatDurationSeconds(d.totalDuration || (d.totalHours || 0) * 3600)}</div>
                </>
            ) : (
                <>
                    <div style={{ color: '#0A2B83' }}>총 생산: {totalProduce.toLocaleString()}개</div>
                    <div style={{ color: '#C04F15' }}>가동율: {Number.isFinite(rate) ? `${rate.toFixed(1)}%` : '-'}</div>
                </>
            )}
        </div>
    );
}



const OPTION_ALL = { value: '', label: '전체 선택' };

function SumDataStatistic() {
    const { selSumDsId } = useOutletContext() || {};
    const processDisplayName = 'summ_data';

    const lastReqKeyRef = useRef('');
    const inFlightRef = useRef(false);

    /* ---- 기간·품목 필터 state (오늘 포함 최근 2주) ---- */
    const [startDate, setStartDate] = useState(moment().subtract(14, 'days').startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
    const [currentProcessStartedAt, setCurrentProcessStartedAt] = useState(null);
    const [modelTypes, setModelTypes] = useState([]);           // 차종 목록 (API)
    const [modelNames, setModelNames] = useState([]);           // 품명 목록 (API)
    const [modelNos, setModelNos] = useState([]);               // 품번 목록 (API)
    const [selectedModelType, setSelectedModelType] = useState(OPTION_ALL);
    const [selectedModelName, setSelectedModelName] = useState(OPTION_ALL);
    const [selectedModelNo, setSelectedModelNo] = useState(OPTION_ALL);
    const [selectPeriod, setSelectPeriod] = useState('recent2Weeks');
    /** 분석 구분: 제품별 가동율 / 비가동 현황 / 작업자별 가동율 (UI만, 백엔드 연동은 추후) */
    const [analysisView, setAnalysisView] = useState('product'); // 'product' | 'downtime' | 'worker'

    /* ---- 차트 데이터 state ---- */
    const [utilData, setUtilData] = useState([]);
    const [downtimeData, setDowntimeData] = useState([]);
    const [isLoadingChart, setIsLoadingChart] = useState(false);
    const [workerBarData, setWorkerBarData] = useState([]);
    /** getUtilBar API 호출 → 모델별 가동율을 utilData에 저장 (필터 선택 사항) */
    const fetchBarData = useCallback(async () => {
        if (!selSumDsId || selSumDsId < 0) {
            setUtilData([]);
            return;
        }
        const formatDate = (date) => {
            if (!date) return moment().format('YYYY-MM-DD');
            const d = moment(date);
            if (!d.isValid()) return moment().format('YYYY-MM-DD');
            return d.format('YYYY-MM-DD');
        };
        const modelTypeVal = selectedModelType != null && typeof selectedModelType === 'object' && 'value' in selectedModelType ? selectedModelType.value : selectedModelType;
        const modelNameVal = selectedModelName != null && typeof selectedModelName === 'object' && 'value' in selectedModelName ? selectedModelName.value : selectedModelName;
        const modelNoVal = selectedModelNo != null && typeof selectedModelNo === 'object' && 'value' in selectedModelNo ? selectedModelNo.value : selectedModelNo;

        try {
            // 전체가 아닐 때만 해당 필터 전달 (차종=전체면 날짜만, 품명=전체면 차종+날짜, 품번=전체면 차종+품명+날짜)
            const rows = await getUtilBar({
                sumDsId: selSumDsId,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                ...(modelTypeVal != null && String(modelTypeVal).trim() !== '' && { modelType: String(modelTypeVal).trim() }),
                ...(modelNameVal != null && String(modelNameVal).trim() !== '' && { modelName: String(modelNameVal).trim() }),
                ...(modelNoVal != null && String(modelNoVal).trim() !== '' && { modelNo: String(modelNoVal).trim() }),
            });
            const rawRows = Array.isArray(rows) ? rows : [];
            const mappedData = rawRows.map((r) => {
                const dateStr = r.date != null ? String(r.date).slice(0, 10) : '-';
                const utilRate = Number(r.utilRate);
                const totalNG = Number(r.totalNG ?? r["totalNG"]) || 0;
                return {
                    name: dateStr,
                    utilRate: isNaN(utilRate) ? 0 : utilRate,
                    totalProduceSum: Number(r.totalProduceSum) || 0,
                    targetProduceSum: Number(r.targetProduceSum) || 0,
                    totalNG,
                };
            });
            setUtilData([...mappedData]);
        } catch (err) {
            console.error('[차트] getUtilBar API error:', err);
            setUtilData([]);
        }
    }, [selSumDsId, startDate, endDate, selectedModelType, selectedModelName, selectedModelNo]);

    const runFetch = useCallback(async () => {
        if (!selSumDsId || selSumDsId < 0) return;
        if (inFlightRef.current) return;

        const fmt = (d) => moment(d).format('YYYY-MM-DD');
        const modelTypeVal = selectedModelType?.value ?? '';
        const modelNameVal = selectedModelName?.value ?? '';
        const modelNoVal = selectedModelNo?.value ?? '';

        const key = JSON.stringify({
            view: analysisView,
            sumDsId: selSumDsId,
            start: fmt(startDate),
            end: fmt(endDate),
            modelType: modelTypeVal,
            modelName: modelNameVal,
            modelNo: modelNoVal,
        });

        if (lastReqKeyRef.current === key) return;

        inFlightRef.current = true;
        setIsLoadingChart(true);

        try {
            if (analysisView === 'worker') {
                const data = await getWorkerBar({
                    sumDsId: selSumDsId,
                    startDate: fmt(startDate),
                    endDate: fmt(endDate),
                    modelType: modelTypeVal,
                    modelName: modelNameVal,
                    modelNo: modelNoVal,
                });
                setWorkerBarData(data || []);
                setDowntimeData([]);
            } else if (analysisView === 'downtime') {
                const data = await getDailyDowntime({
                    sumDsId: selSumDsId,
                    startDate: fmt(startDate),
                    endDate: fmt(endDate),
                })
                setDowntimeData(Array.isArray(data) ? data : []);
                setWorkerBarData([]);
                
            } else {
                await fetchBarData();
                setDowntimeData([]);
            }
            lastReqKeyRef.current = key;
        } catch (err) {
            console.error(err);
        } finally {
            inFlightRef.current = false;
            setIsLoadingChart(false);
        }
    }, [
        analysisView,
        selSumDsId,
        startDate,
        endDate,
        selectedModelType,
        selectedModelName,
        selectedModelNo,
        fetchBarData,
    ]);

    /** 공정 선택 시 차종 목록 로드 */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0) return;
        getModelTypes({ sumDsId: selSumDsId })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelTypes(opts);
          })
          .catch(() => setModelTypes([]));
    }, [selSumDsId]);
    console.log(modelTypes)

    useEffect(() => {
        runFetch();
    }, [runFetch]);

    useEffect(() => {
        if (analysisView !== 'worker') setWorkerBarData([]);
    }, [analysisView]);

    /** 차종 선택 시 품명 목록 로드 (차종=전체면 로드 안 함) */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0 || !selectedModelType || selectedModelType.value === '') {
            setModelNames([]);
            return;
        }
        getModelNames({ sumDsId: selSumDsId, modelType: selectedModelType?.value ?? '' })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelNames(opts);
          })
          .catch(() => setModelNames([]));
    }, [selSumDsId, selectedModelType]);
    

    /** 품명 선택 시 품번 목록 로드 (품명=전체면 로드 안 함) */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0 || !selectedModelName || selectedModelName.value === '') {
            setModelNos([]);
            return;
        }
        getModelNos({ sumDsId: selSumDsId, modelType: selectedModelType?.value ?? '', modelName: selectedModelName?.value ?? '' })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelNos(opts);
          })
          .catch(() => setModelNos([]));
    }, [selSumDsId, selectedModelType, selectedModelName]);

    /** getUtilBar 응답 → 차트에 사용 (백엔드에서 이미 기간 필터됨) */
    const chartData = useMemo(() => {
        if (analysisView === 'worker') {
            return workerBarData || [];
        }
        if (analysisView === 'downtime') {
            return downtimeData || [];
        }
        return utilData || [];
    }, [analysisView, utilData, workerBarData, downtimeData]);

    /** 30개 이상일 때 막대/라인 각각 1개만 라벨 표시 (품질 이력 분석과 동일)
     *  - labelPolicy: 막대(총생산, 비가동 시간)의 최대값 인덱스
     *  - rateLabelPolicy: 가동율(%) 라인의 최대값 인덱스
     */
    const labelPolicy = useMemo(() => {
        const len = chartData?.length ?? 0;
        const mode = getLabelDisplayMode(len);
        if (!len) return { mode, maxIdx: -1 };

        if (analysisView === 'downtime') {
            // 비가동 뷰: 총 비가동 시간(totalHours)이 가장 큰 곳 1개만
            const maxIdx = chartData.reduce((bestIdx, cur, idx) => {
                const curVal = Number(cur.totalHours ?? (cur.plannedHours ?? 0) + (cur.unplannedHours ?? 0)) || 0;
                const bestVal = Number(
                    chartData[bestIdx]?.totalHours ??
                    (chartData[bestIdx]?.plannedHours ?? 0) +
                    (chartData[bestIdx]?.unplannedHours ?? 0)
                ) || 0;
                return curVal > bestVal ? idx : bestIdx;
            }, 0);
            return { mode, maxIdx };
        }

        // 가동율 이력 분석(제품별/작업자별): 막대(총 생산량)가 가장 큰 곳 1개만
        const maxIdx = chartData.reduce((bestIdx, cur, idx) => {
            const curTotal = Number(cur.totalProduceSum) || 0;
            const bestTotal = Number(chartData[bestIdx]?.totalProduceSum) || 0;
            return curTotal > bestTotal ? idx : bestIdx;
        }, 0);
        return { mode, maxIdx };
    }, [chartData, analysisView]);

    const rateLabelPolicy = useMemo(() => {
        const len = chartData?.length ?? 0;
        const mode = getLabelDisplayMode(len);
        if (!len) return { mode, maxIdx: -1 };

        const rateOf = (row) => Number(row?.utilRate != null ? row.utilRate : row?.operatingRate) || 0;
        const maxIdx = chartData.reduce((bestIdx, cur, idx) => {
            const curRate = rateOf(cur);
            const bestRate = rateOf(chartData[bestIdx]);
            return curRate > bestRate ? idx : bestIdx;
        }, 0);
        return { mode, maxIdx };
    }, [chartData]);

    /** 차종/품명/품번 변경 시 BarChart key로 리마운트 (Recharts 렌더 캐시 방지) */
    const chartDataKey = useMemo(
        () =>
            [selectedModelType?.value, selectedModelName?.value, selectedModelNo?.value]
                .filter(Boolean)
                .join('_') || 'all',
        [selectedModelType, selectedModelName, selectedModelNo]
    );

    /** chartData 기준 요약 통계 (제품별: 일자·가동율, 작업자별: 총생산·최다생산/최대가동율 작업자, 비가동: 총/계획외/최다 비가동) */
    const summaryStats = useMemo(() => {
        const arr = chartData || [];
        const rates = arr.map((d) => Number(d.utilRate != null ? d.utilRate : d.operatingRate) || 0).filter((r) => Number.isFinite(r));
        const avgRate = rates.length ? (rates.reduce((a, b) => a + b, 0) / rates.length).toFixed(1) + '%' : '-';
        const minRate = rates.length ? Math.min(...rates).toFixed(1) + '%' : '-';
        const maxRate = rates.length ? Math.max(...rates).toFixed(1) + '%' : '-';
        const minRow = arr.length ? arr.reduce((a, b) => ((Number(a.utilRate != null ? a.utilRate : a.operatingRate) || 0) <= (Number(b.utilRate != null ? b.utilRate : b.operatingRate) || 0) ? a : b), arr[0]) : null;
        const maxRow = arr.length ? arr.reduce((a, b) => ((Number(a.utilRate != null ? a.utilRate : a.operatingRate) || 0) >= (Number(b.utilRate != null ? b.utilRate : b.operatingRate) || 0) ? a : b), arr[0]) : null;
        const minRateDate = minRow?.name != null ? String(minRow.name).slice(0, 10).replace(/-/g, '.') : '-';
        const maxRateDate = maxRow?.name != null ? String(maxRow.name).slice(0, 10).replace(/-/g, '.') : '-';

        const totalProduceSum = arr.reduce((s, d) => s + (Number(d.totalProduceSum) || 0), 0);
        const topProducerRow = arr.length ? arr.reduce((a, b) => ((Number(a.totalProduceSum) || 0) >= (Number(b.totalProduceSum) || 0) ? a : b), arr[0]) : null;
        const topUtilRateRow = arr.length ? arr.reduce((a, b) => ((Number(a.utilRate != null ? a.utilRate : a.operatingRate) || 0) >= (Number(b.utilRate != null ? b.utilRate : b.operatingRate) || 0) ? a : b), arr[0]) : null;
        const topProducerName = topProducerRow?.workerName ?? topProducerRow?.name ?? '-';
        const topProducerQty = topProducerRow != null ? (Number(topProducerRow.totalProduceSum) || 0).toLocaleString() : '-';
        const topUtilRateName = topUtilRateRow?.workerName ?? topUtilRateRow?.name ?? '-';
        const topUtilRateVal = topUtilRateRow != null && Number.isFinite(Number(topUtilRateRow.utilRate != null ? topUtilRateRow.utilRate : topUtilRateRow.operatingRate)) ? `${Number(topUtilRateRow.utilRate != null ? topUtilRateRow.utilRate : topUtilRateRow.operatingRate).toFixed(1)}%` : '-';

        // 비가동 현황용 집계 (totalDuration: 초 단위)
        const totalDowntimeSec = arr.reduce((s, d) => s + (Number(d.totalDuration) || 0), 0);
        const totalUnplannedSec = arr.reduce((s, d) => s + (Number(d.unplannedDuration) || 0), 0);
        const maxDowntimeRow = arr.length ? arr.reduce((a, b) => ((Number(a.totalDuration) || 0) >= (Number(b.totalDuration) || 0) ? a : b), arr[0]) : null;
        const maxUnplannedRow = arr.length ? arr.reduce((a, b) => ((Number(a.unplannedDuration) || 0) >= (Number(b.unplannedDuration) || 0) ? a : b), arr[0]) : null;
        const totalDowntimeFormatted = totalDowntimeSec > 0 ? formatDurationSeconds(totalDowntimeSec) : '-';
        const totalUnplannedFormatted = totalUnplannedSec > 0 ? formatDurationSeconds(totalUnplannedSec) : '-';
        const maxDowntimeFormatted = maxDowntimeRow && (Number(maxDowntimeRow.totalDuration) || 0) > 0 ? formatDurationSeconds(maxDowntimeRow.totalDuration) : '-';
        const maxUnplannedFormatted = maxUnplannedRow && (Number(maxUnplannedRow.unplannedDuration) || 0) > 0 ? formatDurationSeconds(maxUnplannedRow.unplannedDuration) : '-';

        return {
            selectedType: selectedModelType?.label ?? '-',
            selectedName: selectedModelName?.label ?? '-',
            selectedNo: selectedModelNo?.label ?? '-',
            avgUtilRate: avgRate,
            minUtilRate: minRate,
            maxUtilRate: maxRate,
            minUtilRateDate: minRateDate,
            maxUtilRateDate: maxRateDate,
            totalProduceSum,
            topProducerName,
            topProducerQty,
            topUtilRateName,
            topUtilRateVal,
            totalDowntimeFormatted,
            totalUnplannedFormatted,
            maxDowntimeFormatted,
            maxUnplannedFormatted,
        };
    }, [chartData, selectedModelType, selectedModelName, selectedModelNo]);

    /** 선택 기간의 일수 (시작일~종료일 양 끝 포함) */
    const selectedDaysCount = useMemo(() => {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).startOf('day');
        const days = end.diff(start, 'days') + 1;
        return Math.max(0, days);
    }, [startDate, endDate]);

    /** 차트 좌 Y축: 가동율(%) 스케일 - 0~100 고정 */
    const chartScaleLeft = useMemo(() => {
        const step = 25;
        const domainMax = 100;
        const refLines = [];
        for (let y = step; y <= domainMax; y += step) refLines.push(y);
        const ticks = [0, ...refLines];
        return { domainMax, refLines, ticks };
    }, [chartData]);

    /** 차트 우 Y축: 총 생산량(개) - 데이터 최대값 기준 동적, 가동율과 같은 5구간으로 비율 맞춤 */
    const chartScaleRight = useMemo(() => {
        const maxVal = chartData?.length
            ? Math.max(...chartData.map((d) => Number(d.totalProduceSum) || 0))
            : 0;
        const MIN_DOMAIN_MAX = 1000;
        const step = 1000;
        let domainMax = maxVal <= 0 ? MIN_DOMAIN_MAX : Math.ceil(Math.max(maxVal, MIN_DOMAIN_MAX) / step) * step;
        // 가동율 좌축과 동일 5구간: 0, 25%, 50%, 75%, 100%
        const t1 = Math.round(domainMax * 0.25);
        const t2 = Math.round(domainMax * 0.5);
        const t3 = Math.round(domainMax * 0.75);
        const ticks = [0, t1, t2, t3, domainMax];
        return { domainMax, ticks };
    }, [chartData]);

    /** 비가동 현황 차트용 좌 Y축: 가동율(%) 0~100, 우 Y축: 비가동(시간) 0~24 */
    const chartScaleLeftDowntime = useMemo(() => {
        const ticks = [0, 25, 50, 75, 100];
        const refLines = [25, 50, 75, 100];
        return { domainMax: 100, refLines, ticks };
    }, []);

    const chartScaleRightDowntime = useMemo(() => {
        const ticks = [0, 6, 12, 18, 24];
        const refLines = [6, 12, 18, 24];
        return { domainMax: 24, refLines, ticks };
    }, []);

    const getRateLabelOffset = useCallback((props) => {
        const idx = props.index ?? -1;
        const dotY = Number(props.y);
        if (idx < 0 || !Number.isFinite(dotY)) return 24;

        const barLabelVisible = shouldShowChartLabel(labelPolicy, idx);
        if (!barLabelVisible) return 24;

        const row = chartData?.[idx];
        if (!row) return 24;

        const domainMax = analysisView === 'downtime'
            ? chartScaleRightDowntime.domainMax
            : chartScaleRight.domainMax;
        const barValue = analysisView === 'downtime'
            ? Number(row.totalHours ?? (Number(row.plannedHours) || 0) + (Number(row.unplannedHours) || 0)) || 0
            : Number(row.totalProduceSum) || 0;
        if (!domainMax || barValue < 0) return 24;

        const ratio = Math.max(0, Math.min(1, barValue / domainMax));
        const barY = CHART_PLOT_TOP + (1 - ratio) * CHART_PLOT_HEIGHT;
        const barHeight = CHART_PLOT_BOTTOM - barY;
        const showInside = analysisView === 'downtime' ? barHeight >= 24 : barHeight >= 28;
        const barLabelTop = analysisView === 'downtime'
            ? (showInside ? barY + barHeight / 2 - 16 : barY - 36)
            : (showInside ? barY + barHeight / 2 - 20 : barY - 30);
        const barLabelBottom = barLabelTop + 32;

        const baseOffset = 24;
        const rateTop = dotY - baseOffset - 14;
        const rateBottom = dotY - baseOffset + 2;
        const overlaps = rateBottom >= barLabelTop && rateTop <= barLabelBottom;
        if (!overlaps) return baseOffset;

        const safeBaselineY = barLabelTop - 6;
        return Math.max(baseOffset, dotY - safeBaselineY);
    }, [analysisView, chartData, chartScaleRight.domainMax, chartScaleRightDowntime.domainMax, labelPolicy]);

    /** 날짜 개수에 따른 X축 tick interval
     *  - 원칙 1) 차트 가로폭은 카드 너비 고정 (width: 100%)
     *  - 원칙 2) tick은 최대 10개 수준 (XAXIS_MAX_VISIBLE_TICKS)
     *  - 원칙 3) 기본은 2일 간격, 너무 많으면 자동으로 더 띄움
     */
    const chartLayout = useMemo(() => {
        const len = (chartData || []).length;
        if (len <= 0) {
            return { xAxisInterval: 0 };
        }

        // 1) 데이터가 10개 이하면 전부 표시 (간격 1일)
        if (len <= XAXIS_MAX_VISIBLE_TICKS) {
            return { xAxisInterval: 0 };
        }

        // 2) 기본 목표: 2일 간격
        const PREFERRED_DAY_INTERVAL = 2; // 2일 간격 기본
        let dayInterval = PREFERRED_DAY_INTERVAL;
        let estimatedTickCount = Math.ceil(len / dayInterval);

        // 3) tick 개수가 10개를 넘으면, tick 개수가 10개 이하가 되도록 dayInterval 자동 증가
        if (estimatedTickCount > XAXIS_MAX_VISIBLE_TICKS) {
            dayInterval = Math.ceil(len / XAXIS_MAX_VISIBLE_TICKS);
            estimatedTickCount = Math.ceil(len / dayInterval);
        }

        // 안전장치: 최소 2일 간격 이상, interval 값은 dayInterval-1
        if (dayInterval < 2) dayInterval = 2;
        const interval = Math.max(1, dayInterval - 1);

        return { xAxisInterval: interval };
    }, [chartData]);

    /** 적용 버튼: 필터 반영 후 차트 데이터 재조회 */
    const handleApply = async(e) => {
        e?.preventDefault?.();
        runFetch();
    };


    /** 기간 초기화 버튼 */
    const handleResetPeriod = (e) => {
        e?.preventDefault?.();
        setSelectPeriod('recent2Weeks');
        setStartDate(moment().subtract(14, 'days').startOf('day').toDate());
        setEndDate(moment().endOf('day').toDate());
    };

    /* ---- react-select 옵션: 전체 + API 데이터 ---- */
    const modelTypeOptions = useMemo(() => [OPTION_ALL, ...modelTypes.map((t) => (typeof t === 'string' ? { value: t, label: t } : t))], [modelTypes]);
    const modelNameOptions = useMemo(() => {
        if ((selectedModelType?.value ?? '') === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNames.map((n) => (typeof n === 'string' ? { value: n, label: n } : n))];
    }, [modelNames, selectedModelType]);
    const modelNoOptions = useMemo(() => {
        if ((selectedModelName?.value ?? '') === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNos.map((no) => (typeof no === 'string' ? { value: no, label: no } : no))];
    }, [modelNos, selectedModelName]);

    /** 기간 옵션 (품질 이력 분석·데이터관리와 동일) */
    const periodOpt = [
        { label: '시간직접입력', value: 'input' },
        { label: '현재공정', value: 'currentWorks' },
        { label: '최근 2주', value: 'recent2Weeks' },
        { label: '최근 4주', value: 'recent4Weeks' },
    ];

    /** selectPeriod 변경 시 startDate/endDate 동기화 */
    useEffect(() => {
        switch (selectPeriod) {
            case 'input':
                break;
            case 'currentWorks':
                setStartDate(currentProcessStartedAt ? moment(currentProcessStartedAt).startOf('day').toDate() : moment().startOf('day').toDate());
                setEndDate(moment().endOf('day').toDate());
                break;
            case 'recent2Weeks':
                setStartDate(moment().subtract(14, 'days').startOf('day').toDate());
                setEndDate(moment().endOf('day').toDate());
                break;
            case 'recent4Weeks':
                setStartDate(moment().subtract(28, 'days').startOf('day').toDate());
                setEndDate(moment().endOf('day').toDate());
                break;
            default:
                setStartDate(moment().startOf('day').toDate());
                setEndDate(moment().endOf('day').toDate());
                break;
        }
    }, [selectPeriod, currentProcessStartedAt]);

    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0) {
            setCurrentProcessStartedAt(null);
            return;
        }
        let mounted = true;
        getCurrentProcessStartedAt({ sumDsId: selSumDsId })
            .then((startedAt) => {
                if (mounted) setCurrentProcessStartedAt(startedAt ?? null);
            })
            .catch((err) => {
                console.error('getCurrentProcessStartedAt failed:', err);
                if (mounted) setCurrentProcessStartedAt(null);
            });
        return () => { mounted = false; };
    }, [selSumDsId]);

    if (selSumDsId == null || selSumDsId < 0) {
        return <NoData />;
    }

    return (
        <div className="operationRateStatistic">
            <p className="path">데이터 분석<span className="below">{'>'}</span>가동율 이력 분석</p>
            <h2 className="pageTitle">가동율 이력 분석 - {processDisplayName}</h2>

            <div className="operationRateContent">
            {/* 필터 카드: 기간, 품목(차종/품명/품번), 분석 조건 */}
            <div className="filterCard">
                <form onSubmit={handleApply}>
                    {/* 1행: 기간, 날짜, 현재공정, 적용, 초기화 */}
                    <div className={`filterRow filterRow1 ${analysisView === 'downtime' ? 'downtimeView' : ''}`}>
                        <span className="datePickerLabel">기간</span>
                        <div className="datePickerArea">
                            <div className="startDateArea">
                                <CustomDateOnlyPickerStart
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    changeDropdown={() => setSelectPeriod('input')}
                                />
                            </div>
                            <span>-</span>
                            <div className="endDateArea">
                                <CustomDateOnlyPickerEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    changeDropdown={() => setSelectPeriod('input')}
                                />
                            </div>
                        </div>
                        <Select
                            options={periodOpt}
                            value={periodOpt.find((el) => el.value === selectPeriod)}
                            onChange={(sel) => setSelectPeriod(sel.value)}
                            isSearchable={false}
                            className="filterSelect periodSelect periodOnly"
                            classNames={{
                                control: (state) => (state.isFocused ? 'selectControl focused' : 'selectControl'),
                                container: () => 'selectBox',
                                menu: () => 'selectMenu',
                                menuList: () => 'optionsList',
                                option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                singleValue: () => 'singleVal',
                                valueContainer: () => 'valueContainer',
                            }}
                            components={{
                                DropdownIndicator: (props) => (
                                    <components.DropdownIndicator {...props}>
                                        <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                            <img src={filledArrowIconImg} alt="드롭다운" />
                                        </span>
                                    </components.DropdownIndicator>
                                ),
                                IndicatorSeparator: () => null,
                            }}
                        />
                        <div className="filterRow1Right">
                            <button type="submit" className="applyBtn">적용</button>
                            <button type="button" className="resetPeriodBtn" onClick={handleResetPeriod}>
                                <span>기간 초기화</span>
                                <img src={resetIconImg} alt="초기화" />
                            </button>
                        </div>
                        {/* 분석 조건: 기간 초기화 오른쪽에 배치 */}
                        <div className="analysisSection">
                            <span className="analysisConditionLabel">분석 조건</span>
                            <div className="analysisRadioRow" role="group" aria-label="분석 구분">
                                <label className={`analysisRadioItem ${analysisView === 'product' ? 'selected' : ''}`}>
                                    <input type="radio" name="analysisView" value="product" checked={analysisView === 'product'} onChange={() => setAnalysisView('product')} />
                                    <span className="radioCircle" />
                                    <span className="radioLabel">제품별 가동율</span>
                                </label>
                                <label className={`analysisRadioItem ${analysisView === 'downtime' ? 'selected' : ''}`}>
                                    <input type="radio" name="analysisView" value="downtime" checked={analysisView === 'downtime'} onChange={() => setAnalysisView('downtime')} />
                                    <span className="radioCircle" />
                                    <span className="radioLabel">비가동 현황</span>
                                </label>
                                <label className={`analysisRadioItem ${analysisView === 'worker' ? 'selected' : ''}`}>
                                    <input type="radio" name="analysisView" value="worker" checked={analysisView === 'worker'} onChange={() => setAnalysisView('worker')} />
                                    <span className="radioCircle" />
                                    <span className="radioLabel">작업자별 가동율</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    {/* 2행: 품목 */}
                    <div className="filterRow filterRow2">
                        {analysisView !== 'downtime' && (
                            <div className="itemSection">
                                <span className="filterLabel">품목</span>
                                <div className="selectGroup">
                                    <div className="selectItem">
                                        <span className="selectItemLabel">차종</span>
                                        <Select
                                            options={modelTypeOptions}
                                            value={selectedModelType}
                                            onChange={(v) => {
                                                setSelectedModelType(v);
                                                setSelectedModelName(OPTION_ALL);
                                                setSelectedModelNo(OPTION_ALL);
                                            }}
                                            placeholder="전체 선택"
                                            isSearchable={false}
                                            classNames={{
                                                control: () => 'selectControl',
                                                container: () => 'selectBox',
                                                menu: () => 'selectMenu',
                                                menuList: () => 'optionsList',
                                                option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                                singleValue: () => 'singleVal',
                                                valueContainer: () => 'valueContainer',
                                                placeholder: () => 'selectPlaceholder',
                                            }}
                                            components={{
                                                DropdownIndicator: (props) => (
                                                    <components.DropdownIndicator {...props}>
                                                        <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                            <img src={filledArrowIconImg} alt="드롭다운" />
                                                        </span>
                                                    </components.DropdownIndicator>
                                                ),
                                                IndicatorSeparator: () => null,
                                            }}
                                        />
                                    </div>
                                    <div className="selectItem">
                                        <span className="selectItemLabel">품명</span>
                                        <Select
                                            options={modelNameOptions}
                                            value={selectedModelName}
                                            onChange={(v) => {
                                                setSelectedModelName(v);
                                                setSelectedModelNo(OPTION_ALL);
                                            }}
                                            placeholder="전체 선택"
                                            isSearchable={false}
                                            isDisabled={(selectedModelType?.value ?? '') === ''}
                                            classNames={{
                                                control: (state) => state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                                container: () => 'selectBox',
                                                menu: () => 'selectMenu',
                                                menuList: () => 'optionsList',
                                                option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                                singleValue: () => 'singleVal',
                                                valueContainer: () => 'valueContainer',
                                                placeholder: () => 'selectPlaceholder',
                                            }}
                                            components={{
                                                DropdownIndicator: (props) => (
                                                    <components.DropdownIndicator {...props}>
                                                        <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                            <img src={filledArrowIconImg} alt="드롭다운" />
                                                        </span>
                                                    </components.DropdownIndicator>
                                                ),
                                                IndicatorSeparator: () => null,
                                            }}
                                        />
                                    </div>
                                    <div className="selectItem">
                                        <span className="selectItemLabel">품번</span>
                                        <Select
                                            options={modelNoOptions}
                                            value={selectedModelNo}
                                            onChange={(v) => setSelectedModelNo(v)}
                                            placeholder="전체 선택"
                                            isSearchable={false}
                                            isDisabled={(selectedModelName?.value ?? '') === ''}
                                            classNames={{
                                                control: (state) => state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                                container: () => 'selectBox',
                                                menu: () => 'selectMenu',
                                                menuList: () => 'optionsList',
                                                option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                                singleValue: () => 'singleVal',
                                                valueContainer: () => 'valueContainer',
                                                placeholder: () => 'selectPlaceholder',
                                            }}
                                            components={{
                                                DropdownIndicator: (props) => (
                                                    <components.DropdownIndicator {...props}>
                                                        <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                            <img src={filledArrowIconImg} alt="드롭다운" />
                                                        </span>
                                                    </components.DropdownIndicator>
                                                ),
                                                IndicatorSeparator: () => null,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </form>
            </div>

            {/* 차트 (카드 바로 위는 차트만, 요약 카드는 그 아래) */}
            <div className="chartCard">
                {/* <div className="chartTitle">{analysisView === 'downtime' ? '가동율 및 비가동 현황' : '가동율'}</div> */}
                <div className="chartWrapper" style={{ overflowX: 'hidden', overflowY: 'hidden' }}>
                    {isLoadingChart ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_HEIGHT, color: '#888', fontSize: '14px' }}>
                            데이터를 분석 중입니다. 잠시만 기다려 주세요
                        </div>
                    ) : chartData && chartData.length > 0 ? (
                        analysisView === 'downtime' ? (
                            <div className="chartInnerWrap" style={{ width: '100%', minWidth: '100%' }}>
                                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                                    <ComposedChart
                                        key={`downtime_${chartDataKey}`}
                                        data={chartData}
                                        margin={{ top: 32, right: 70, left: 70, bottom: 24 }}
                                        barCategoryGap={`${BAR_CATEGORY_GAP_PERCENT}%`}
                                        barSize={Math.min(48, Math.max(24, BAR_WIDTH_PER_ITEM_PX - 12))}
                                    >
                                        <XAxis
                                            dataKey="name"
                                            axisLine={{ stroke: '#000000' }}
                                            interval={chartLayout.xAxisInterval}
                                            angle={0}
                                            textAnchor="middle"
                                            height={56}
                                            tick={{ fontFamily: 'NanumSquare', fontSize: XAXIS_TICK_FONT_SIZE, fill: '#4D4D4D' }}
                                            tickFormatter={(val) => (val != null && String(val).trim() !== '' ? String(val).split('T')[0].replace(/-/g, '.') : '')}
                                            label={(props) => {
                                                const { viewBox } = props;
                                                if (!viewBox || viewBox.width == null) return null;
                                                return (
                                                    <g transform={`translate(${viewBox.x + viewBox.width - 8}, ${viewBox.y + viewBox.height -15})`}>
                                                        <text
                                                            x={0}
                                                            y={0}
                                                            textAnchor="middle"
                                                            fill="#000"
                                                            fontFamily="NanumSquare"
                                                            fontSize={12}
                                                        >
                                                            일자
                                                        </text>
                                                    </g>
                                                );
                                            }}
                                        />
                                        <YAxis yAxisId="left" orientation="left" width={70} domain={[0, chartScaleLeftDowntime.domainMax]} ticks={chartScaleLeftDowntime.ticks} axisLine={{ stroke: '#000000' }} tick={{ fontFamily: 'NanumSquare', fontSize: 12, fill: '#4D4D4D' }} tickFormatter={(v) => `${v}%`}>
                                            <Label content={(props) => <CenterYAxisLabel {...props} side="left" value="가동율 (%)" fill="#C04F15" />} />
                                        </YAxis>
                                        <YAxis yAxisId="right" orientation="right" width={70} domain={[0, chartScaleRightDowntime.domainMax]} ticks={chartScaleRightDowntime.ticks} axisLine={{ stroke: '#000000' }} tick={{ fontFamily: 'NanumSquare', fontSize: 12, fill: '#4D4D4D' }} tickFormatter={(v) => String(v)}>
                                            <Label content={(props) => <CenterYAxisLabel {...props} side="right" value="비가동 (시간)" fill="#0A2B83" />} />
                                        </YAxis>
                                        <Tooltip content={<UtilCustomTooltip />} />
                                        <CartesianGrid horizontal={true} vertical={false} stroke="#d0d0d0" strokeDasharray="4 4" strokeWidth={1} />
                                        <Line yAxisId="left" type="monotone" dataKey="operatingRate" name="가동율" stroke="#C04F15" strokeWidth={2} dot={{ fill: '#C04F15', r: 4 }} activeDot={{ r: 6 }}>
                                            <LabelList dataKey="operatingRate" content={(props) => {
                                                const idx = props.index ?? -1;
                                                if (!shouldShowChartLabel(rateLabelPolicy, idx)) return null;
                                                return <UtilRateLineLabel {...props} yOffset={getRateLabelOffset(props)} />;
                                            }} />
                                        </Line>
                                        <Bar yAxisId="right" dataKey="unplannedHours" name="계획 외 비가동" stackId="dt" fill="#0A2B83" fillOpacity={BAR_FILL_OPACITY} radius={[0, 0, 0, 0]} stroke="#0A2B83" strokeWidth={0.5}>
                                            <LabelList dataKey="unplannedHours" content={(props) => {
                                                const idx = props.index ?? -1;
                                                if (!shouldShowChartLabel(labelPolicy, idx)) return null;
                                                return <DowntimeUnplannedLabel {...props} />;
                                            }} />
                                        </Bar>
                                        <Bar yAxisId="right" dataKey="plannedHours" name="계획" stackId="dt" shape={<DowntimePlannedBarShape />} radius={[0, 0, 0, 0]}>
                                            <LabelList dataKey="totalHours" content={(props) => {
                                                const idx = props.index ?? -1;
                                                if (!shouldShowChartLabel(labelPolicy, idx)) return null;
                                                return <DowntimeTotalLabel {...props} />;
                                            }} />
                                        </Bar>
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                        <div className="chartInnerWrap" style={{ width: '100%', minWidth: '100%' }}>
                            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                                <ComposedChart
                                    key={chartDataKey}
                                    data={chartData}
                                    margin={{ top: 20, right: 90, left: 70, bottom: 24 }}
                                    barCategoryGap={`${BAR_CATEGORY_GAP_PERCENT}%`}
                                    barSize={Math.min(48, Math.max(24, BAR_WIDTH_PER_ITEM_PX - 12))}
                                >
                                    <XAxis
                                        dataKey={analysisView === 'worker' ? 'workerName' : 'name'}
                                        axisLine={{ stroke: '#000000' }}
                                        interval={chartLayout.xAxisInterval}
                                        angle={analysisView === 'worker' ? 0 : 0}
                                        textAnchor="middle"
                                        height={56}
                                        tick={{ fontFamily: 'NanumSquare', fontSize: XAXIS_TICK_FONT_SIZE, fill: '#4D4D4D' }}
                                        tickFormatter={(val) => (val != null && String(val).trim() !== '' ? String(val).split('T')[0].replace(/-/g, '.') : '')}
                                        label={(props) => {
                                            const { viewBox } = props;
                                            if (!viewBox || viewBox.width == null) return null;
                                            return (
                                                <g transform={`translate(${viewBox.x + viewBox.width - 8}, ${viewBox.y + viewBox.height -15})`}>
                                                    <text
                                                        x={0}
                                                        y={0}
                                                        textAnchor="end"
                                                        fill="#000"
                                                        fontFamily="NanumSquare"
                                                        fontSize={12}
                                                    >
                                                        작업자
                                                    </text>
                                                </g>
                                            );
                                        }}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        orientation="left"
                                        width = {70}
                                        domain={[0, chartScaleLeft.domainMax]}
                                        ticks={chartScaleLeft.ticks}
                                        axisLine={{ stroke: '#000000' }}
                                        tick={{ fontFamily: 'NanumSquare', fontSize: 12, fill: '#4D4D4D' }}
                                        tickFormatter={(v) => `${v}%`}
                                        >
                                        <Label
                                            content={(props) => (
                                            <CenterYAxisLabel {...props} side="left" value="가동율(%)" fill="#C04F15" />
                                            )}
                                        />
                                        </YAxis>
                                        <YAxis
                                            yAxisId="right"
                                            orientation="right"
                                            width = {80}
                                            domain={[0, chartScaleRight.domainMax]}
                                            ticks={chartScaleRight.ticks}
                                            axisLine={{ stroke: '#000000' }}
                                            tick={{ fontFamily: 'NanumSquare', fontSize: 12, fill: '#4D4D4D' }}
                                            tickFormatter={(v) => v.toLocaleString()}
                                            >
                                            <Label
                                                content={(props) => (
                                                <CenterYAxisLabel {...props} side="right" value="총 생산량(개)" fill="#0A2B83" />
                                                )}
                                            />
                                            </YAxis>
                                    <Tooltip content={<UtilCustomTooltip analysisView={analysisView} />} />
                                    {/* 점선 그리드: 좌 Y축(가동율)만 사용해서 라인 중복 제거 */}
                                    {chartScaleLeft.refLines.map((y) => (
                                        <ReferenceLine
                                            key={`left-${y}`}
                                            yAxisId="left"
                                            y={y}
                                            stroke="#d0d0d0"
                                            strokeDasharray="4 4"
                                            strokeWidth={1}
                                        />
                                    ))}
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="utilRate"
                                        name="가동율"
                                        stroke="#C04F15"
                                        strokeWidth={2}
                                        dot={{ fill: '#C04F15', r: 4 }}
                                        activeDot={{ r: 6 }}
                                    >
                                        <LabelList
                                            dataKey="utilRate"
                                            content={(props) => {
                                                const idx = props.index ?? -1;
                                                if (!shouldShowChartLabel(rateLabelPolicy, idx)) return null;
                                                return <UtilRateLineLabel {...props} yOffset={getRateLabelOffset(props)} />;
                                            }}
                                        />
                                    </Line>
                                    <Bar
                                        yAxisId="right"
                                        dataKey="totalProduceSum"
                                        name="총 생산"
                                        fill="#0A2B83"
                                        fillOpacity={BAR_FILL_OPACITY}
                                        radius={[0, 0, 0, 0]}
                                    >
                                        <LabelList
                                            dataKey="totalProduceSum"
                                            content={(props) => {
                                                const idx = props.index ?? -1;
                                                if (!shouldShowChartLabel(labelPolicy, idx)) return null;
                                                return <TotalLabelOnTopSegment {...props} />;
                                            }}
                                        />
                                    </Bar>
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                        )
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: CHART_HEIGHT,
                            color: '#888',
                            fontSize: '14px',
                        }}>
                            {analysisView === 'downtime' ? '선택한 기간에 비가동 데이터가 없습니다.' : '선택한 기간에 가동율 데이터가 없습니다.'}
                        </div>
                    )}
                </div>
            </div>

            {/* 요약 통계 카드: 비가동 4항목 / 제품별(가동율 값 강조) / 작업자별 4항목 - 품질 이력 분석과 동일한 형식(항목 / : / 값) */}
            <div className="summaryCard">
                <ul className={`summaryList summaryList--${analysisView === 'worker' ? 'worker' : analysisView === 'downtime' ? 'downtime' : 'product'}`}>
                    {analysisView === 'worker' ? (
                        <>
                            <li>
                                <span className="summaryKey">ᆞ총 선택 일자</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{selectedDaysCount}일</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ총 생산 수량</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">
                                    {typeof summaryStats.totalProduceSum === 'number'
                                        ? summaryStats.totalProduceSum.toLocaleString()
                                        : summaryStats.totalProduceSum}
                                    개
                                </span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ최다 생산 작업자 / 생산량</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">
                                    {summaryStats.topProducerName} / {summaryStats.topProducerQty}
                                </span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ최대 가동율 작업자 / 가동율</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">
                                    {summaryStats.topUtilRateName} / {summaryStats.topUtilRateVal}
                                </span>
                            </li>
                        </>
                    ) : analysisView === 'downtime' ? (
                        <>
                            <li>
                                <span className="summaryKey">ᆞ총 선택 일자</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{selectedDaysCount}일</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ평균 가동율</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{summaryStats.avgUtilRate}</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ총 비가동 시간</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{summaryStats.totalDowntimeFormatted}</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ총 계획 외 비가동 시간</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{summaryStats.totalUnplannedFormatted}</span>
                            </li>
                        </>
                    ) : (
                        <>
                            <li>
                                <span className="summaryKey">ᆞ총 선택 일자</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{selectedDaysCount}일</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ총 생산 수량</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">
                                    {typeof summaryStats.totalProduceSum === 'number'
                                        ? summaryStats.totalProduceSum.toLocaleString()
                                        : summaryStats.totalProduceSum}
                                    개
                                </span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ평균 가동율</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{summaryStats.avgUtilRate}</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ최대 가동율</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{summaryStats.maxUtilRate}</span>
                            </li>
                            <li>
                                <span className="summaryKey">ᆞ최소 가동율</span>
                                <span className="summaryColon"> : </span>
                                <span className="summaryVal">{summaryStats.minUtilRate}</span>
                            </li>
                        </>
                    )}
                </ul>
            </div>
            </div>
        </div>
    );
}

export default SumDataStatistic;
